import {
  SIDEBAR_CATEGORIES,
  type SidebarCategoryId,
  workflowBuilderRegistryFields,
  type WorkflowRegistryFields,
} from './Sidebar/sidebarPalette';
import { slugifyAgentNodeName } from './agentWorkflowTypes';

export type FlowFxKindId =
  | 'if-else'
  | 'switch'
  | 'loop'
  | 'parallel'
  | 'merge'
  | 'end'
  | 'error-handler'
  | 'set-variable'
  | 'transform'
  | 'logger'
  | 'comment';

export type FlowFxCategoryTag = 'flow' | 'utility';

export type FlowFxPersistedData = {
  label: string;
  paletteType: string;
  kind: FlowFxKindId;
  nodeName: string;
  categoryTag: FlowFxCategoryTag;
  incomingInputHint?: string;

  ifConditionExpr: string;
  ifTrueLabel: string;
  ifFalseLabel: string;

  switchExpr: string;
  switchCases: { id: string; match: string; label: string }[];

  loopArrayExpr: string;
  loopItemVar: string;
  loopContinueOnError: boolean;
  loopParallel: boolean;
  loopMaxParallel: string;

  parallelBranchCount: 2 | 3 | 4 | 5;
  parallelLabels: string[];

  mergeInputCount: 2 | 3 | 4 | 5;
  mergeStrategy: 'all' | 'any' | 'n-of-m';
  mergeN: string;
  mergeM: string;
  mergeTimeoutSec: string;
  mergeOnTimeout: 'continue' | 'fail' | 'defaults';

  endReturnKind: 'none' | 'last' | 'custom';
  endCustomExpr: string;
  endStatus: 'success' | 'failed' | 'cancelled';

  setVariableRows: { id: string; name: string; value: string }[];

  transformInputExpr: string;
  transformType: 'map' | 'filter' | 'reduce' | 'sort' | 'pick' | 'custom';
  transformExpr: string;

  utilLogLevel: 'debug' | 'info' | 'warn' | 'error';
  utilLogMessage: string;
  utilLogPayload: boolean;
  utilLogTimestamp: boolean;
  utilLogNodePath: boolean;

  commentTitle: string;
  commentBody: string;
  commentAccent: 'gray' | 'blue' | 'green' | 'amber' | 'pink';
} & WorkflowRegistryFields;

export function newFlowFxRowId(): string {
  return `fx-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function slugifyFlowFxNodeName(raw: string): string {
  return slugifyAgentNodeName(raw);
}

export function isFlowControlCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.FLOW_CONTROL || category === SIDEBAR_CATEGORIES.TERMINAL;
}

export function isUtilitiesCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.UTILITIES;
}

export function paletteTypeToFlowFxKind(paletteType: string): FlowFxKindId | null {
  const map: Record<string, FlowFxKindId> = {
    Flow_IfElse: 'if-else',
    Flow_Switch: 'switch',
    Flow_Loop: 'loop',
    Flow_Parallel: 'parallel',
    Flow_Merge: 'merge',
    End: 'end',
    Flow_End: 'end',
    Flow_ErrorHandler: 'error-handler',
    Util_SetVariable: 'set-variable',
    Util_TransformData: 'transform',
    Util_Logger: 'logger',
    Util_Comment: 'comment',
  };
  return map[paletteType] ?? null;
}

export function flowFxBadge(kind: FlowFxKindId): string {
  const m: Record<FlowFxKindId, string> = {
    'if-else': 'COND',
    switch: 'CASE',
    loop: 'ITER',
    parallel: 'SPLIT',
    merge: 'JOIN',
    end: 'STOP',
    'error-handler': 'CATCH',
    'set-variable': 'VAR',
    transform: 'MAP',
    logger: 'DEBUG',
    comment: 'NOTE',
  };
  return m[kind] ?? 'FX';
}

export function flowFxHasTargetHandle(kind: FlowFxKindId): boolean {
  return kind !== 'comment';
}

export function flowFxHasSourceHandle(kind: FlowFxKindId): boolean {
  return kind !== 'end' && kind !== 'comment';
}

export function flowFxOutputHints(d: FlowFxPersistedData): string[] {
  const n = d.nodeName;
  switch (d.kind) {
    case 'if-else':
      return [`{{ ${n}.result }}`, `{{ ${n}.branch }}`];
    case 'switch':
      return [`{{ ${n}.matchedCase }}`];
    case 'loop':
      return [
        '{{ loop.item }}',
        '{{ loop.index }}',
        '{{ loop.isFirst }}',
        '{{ loop.isLast }}',
        '{{ loop.total }}',
        `{{ ${n}.results }}`,
        `{{ ${n}.errors }}`,
      ];
    case 'parallel':
      return [`{{ ${n}.branch }}`, ...d.parallelLabels.slice(0, d.parallelBranchCount).map((_, i) => `{{ ${n}.path${i} }}`)];
    case 'merge':
      return [`{{ ${n}.results }}`, `{{ ${n}.completed }}`, `{{ ${n}.timedOut }}`];
    case 'end':
      return [];
    case 'error-handler':
      return [`{{ ${n}.success }}`, `{{ ${n}.error }}`];
    case 'set-variable':
      return d.setVariableRows.filter((r) => r.name.trim()).map((r) => `{{ ${n}.${r.name.trim()} }}`);
    case 'transform':
      return [`{{ ${n}.output }}`];
    case 'logger':
      return [`{{ ${n}.output }}`];
    case 'comment':
      return [];
    default:
      return [`{{ ${n}.output }}`];
  }
}

export function defaultParallelLabels(count: number): string[] {
  const letters = ['A', 'B', 'C', 'D', 'E'];
  return letters.slice(0, count).map((L) => `Path ${L}`);
}

export function createDefaultFlowFxData(
  payload: { type: string; label: string } & WorkflowRegistryFields
): FlowFxPersistedData | null {
  const kind = paletteTypeToFlowFxKind(payload.type);
  if (!kind) return null;
  const isFlow =
    kind !== 'set-variable' && kind !== 'transform' && kind !== 'logger' && kind !== 'comment';
  const categoryTag: FlowFxCategoryTag = isFlow ? 'flow' : 'utility';
  const nodeName = slugifyFlowFxNodeName(payload.label);

  const base: FlowFxPersistedData = {
    label: payload.label,
    paletteType: payload.type,
    kind,
    nodeName,
    categoryTag,
    ifConditionExpr: '{{ score }} > 80',
    ifTrueLabel: 'TRUE',
    ifFalseLabel: 'FALSE',
    switchExpr: '{{ lead.grade }}',
    switchCases: [
      { id: newFlowFxRowId(), match: 'A', label: 'Hot Lead' },
      { id: newFlowFxRowId(), match: 'B', label: 'Warm Lead' },
      { id: newFlowFxRowId(), match: 'C', label: 'Nurture' },
    ],
    loopArrayExpr: '{{ leads }}',
    loopItemVar: 'lead',
    loopContinueOnError: true,
    loopParallel: false,
    loopMaxParallel: '5',
    parallelBranchCount: 3,
    parallelLabels: defaultParallelLabels(5),
    mergeInputCount: 3,
    mergeStrategy: 'all',
    mergeN: '2',
    mergeM: '3',
    mergeTimeoutSec: '60',
    mergeOnTimeout: 'continue',
    endReturnKind: 'custom',
    endCustomExpr: '{\n  "status": "complete",\n  "result": {{ agent.out }}\n}',
    endStatus: 'success',
    setVariableRows: [
      { id: newFlowFxRowId(), name: 'total_score', value: '{{ a }} + {{ b }}' },
      { id: newFlowFxRowId(), name: 'is_qualified', value: '{{ score }} > 80' },
    ],
    transformInputExpr: '{{ leads }}',
    transformType: 'map',
    transformExpr: 'item => ({\n  id: item.id,\n  score: item.score,\n  qualified: item.score > 80\n})',
    utilLogLevel: 'info',
    utilLogMessage: 'Processing {{ lead.id }}',
    utilLogPayload: true,
    utilLogTimestamp: false,
    utilLogNodePath: false,
    commentTitle: payload.label,
    commentBody: 'Document this section of the workflow.',
    commentAccent: 'gray',
  };

  if (kind === 'comment') {
    base.commentTitle = payload.label;
  }
  if (kind === 'end') {
    base.endReturnKind = 'custom';
  }
  if (kind === 'logger') {
    base.utilLogMessage = `Checkpoint: ${payload.label}`;
  }

  return {
    ...base,
    ...workflowBuilderRegistryFields({
      category: payload.workflowCategoryid,
      controlUuid: payload.workflowControlid,
      categoryUuid: payload.workflowCategoryuuid,
    }),
  };
}

export function buildFlowFxPreviewLines(d: FlowFxPersistedData): string[] {
  switch (d.kind) {
    case 'if-else':
      return [d.ifConditionExpr.trim() || '—'];
    case 'switch':
      return [`on: ${d.switchExpr.trim() || '—'}`];
    case 'loop':
      return [
        `for each in ${d.loopArrayExpr.trim() || '—'}`,
        `item: {{ ${d.loopItemVar || 'item'} }}`,
      ];
    case 'parallel':
      return ['Execute simultaneously:', ...d.parallelLabels.slice(0, d.parallelBranchCount)];
    case 'merge':
      return [
        `Wait: ${d.mergeStrategy}${d.mergeStrategy === 'n-of-m' ? ` (${d.mergeN}/${d.mergeM})` : ''}`,
        `Timeout: ${d.mergeTimeoutSec}s`,
      ];
    case 'end':
      return [
        d.endReturnKind === 'custom'
          ? truncate(d.endCustomExpr.replace(/\s+/g, ' '), 48)
          : d.endReturnKind === 'last'
            ? 'Last node output'
            : 'No return value',
        `Status: ${d.endStatus}`,
      ];
    case 'error-handler':
      return ['Try / catch wrapper', 'Success · Error'];
    case 'set-variable':
      return d.setVariableRows.slice(0, 3).map((r) => `${r.name || '?'} = ${truncate(r.value, 28)}`);
    case 'transform':
      return [truncate(d.transformInputExpr, 36), `${d.transformType}: …`];
    case 'logger':
      return [`${d.utilLogLevel.toUpperCase()}`, truncate(d.utilLogMessage, 40)];
    case 'comment':
      return [d.commentTitle ? `📝 ${d.commentTitle}` : '📝', truncate(d.commentBody, 72)];
    default:
      return ['—'];
  }
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

export function defaultSwitchCases(): { id: string; match: string; label: string }[] {
  return [
    { id: newFlowFxRowId(), match: 'A', label: 'Case A' },
    { id: newFlowFxRowId(), match: 'B', label: 'Case B' },
  ];
}
